import { Button, Card, Divider, Form, Modal, Result } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import moment, { Moment } from 'moment';

import GlobirdSelector from 'components/Shared/GlobirdSelector';
import DatePicker from 'components/Shared/DatePicker';
import {
    BillExtensionEligibilityDto,
    CalendarControlDateFormat,
    StateCode
} from 'data/CustomerPortalTypes';
import links from 'data/Links';
import { addBillExtension, getBillExtensionEligibility } from 'services/RequestDataService';
import { selectCurrentAccount } from 'store/userSlice';
import { isDisabledDate } from 'utilities/DateTool';
import { formatDisplayDate, generateRequiredRule, getRuleObject } from 'utilities/Utility';

import menuOptions from '../MenuOptions';
import styles from './BillExtension.module.css';

const unavailableMessage = <>Bill extension is currently unavailable online, please <Link to="/assistance">contact us</Link>.</>;

const vicPaymentAssistance = [
    'If you\'re having trouble paying, or think you might, you\'re entitled to payment assistance.',
    'Before you fall into arrears, you can request a payment extension, make equal payments over time, or pay in advance.',
    'If you\'re in arrears, you\'re entitled to Tailored Assistance, including a payment plan covering your ongoing energy use and repayment of your arrears over up to two years. You\'re also entitled to advice and practical help to lower your energy costs, and help with concessions, Utility Relief Grants and other government or community assistance.',
    'If you can\'t afford the full cost of your ongoing energy use, you\'re also entitled to at least six months where repayment of your arrears is put on hold and you can pay less than the full cost of your energy use while you work with us to reduce your energy costs.',
    'If you\'re receiving Tailored Assistance, we\'ll also move you to our best eligible offer if required.'
];

const aerPaymentAssistance = [
    'If you\'re having trouble paying, you\'re entitled to payment assistance.',
    'We\'ll work out a payment plan by considering all relevant factors, including, the amount you owe, what you can afford, and your expected energy use.',
    'If you\'re experiencing hardship, we can also help with concessions, rebates or grants, Centrepay, help you find a financial counsellor, and practical ways to reduce your energy use and costs.'
];

const getPaymentAssistance = (state?: string) => {
    if (!state) {
        return null;
    }

    return state === StateCode.VIC ? vicPaymentAssistance : aerPaymentAssistance;
};

const BillExtension = () => {
    const currentAccount = useSelector(selectCurrentAccount);
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const accountId = currentAccount?.accountId;
    const requestContext = useRef({ active: false, submitting: false });
    const [eligibility, setEligibility] = useState<BillExtensionEligibilityDto | null>();
    const [newDueDate, setNewDueDate] = useState<Moment | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [submissionResult, setSubmissionResult] = useState<'success' | 'error'>();

    useEffect(() => {
        const context = { active: true, submitting: false };
        requestContext.current = context;
        setSubmitting(false);
        setEligibility(undefined);
        setNewDueDate(null);
        setSubmissionResult(undefined);

        if (!accountId) {
            return () => { context.active = false; };
        }

        getBillExtensionEligibility(accountId)
            .then(result => {
                if (!context.active) {
                    return;
                }

                const data = result?.success ? result.data ?? null : null;
                setEligibility(data);
            });

        return () => { context.active = false; };
    }, [accountId]);

    const submit = async ({ newDueDate: date }: { newDueDate: Moment }) => {
        const context = requestContext.current;
        if (!context.active || context.submitting || !accountId || !accountState
            || !eligibility?.isEligible || !eligibility.invoiceGuid || !calendar
            || !date?.isValid() || isDisabledDate(date, calendar)) {
            return;
        }

        context.submitting = true;
        setSubmitting(true);
        setSubmissionResult(undefined);

        try {
            const result = await addBillExtension(accountId, {
                invoiceGuid: eligibility.invoiceGuid,
                newDueDate: date.format(CalendarControlDateFormat)
            });

            if (context.active) {
                setSubmissionResult(result?.success ? 'success' : 'error');
            }
        }
        catch {
            if (context.active) {
                setSubmissionResult('error');
            }
        }
        finally {
            context.submitting = false;
            if (context.active) {
                setSubmitting(false);
            }
        }
    };

    const calendar = eligibility?.calendarControl;
    const accountState = currentAccount?.services.find(service => service.state)?.state;
    const paymentAssistance = getPaymentAssistance(accountState);

    const renderContent = () => {
        if (eligibility === undefined) {
            return null;
        }

        if (!eligibility?.isEligible || !eligibility.invoiceGuid || !eligibility.currentDueDate || !calendar || !accountState) {
            return <Result status="warning" subTitle={unavailableMessage} />;
        }

        return <>
            <Form key={accountId} preserve={false} className="field-container" form={form} name="billExtensionForm" layout="vertical" onFinish={submit}>
                <Form.Item label="Current Due Date" className={styles.currentDueDate}>
                    <b>{formatDisplayDate(moment(eligibility.currentDueDate, CalendarControlDateFormat, true))}</b>
                </Form.Item>
                <Divider className={styles.dateDivider} />
                <Form.Item
                    label="New Due Date"
                    name="newDueDate"
                    extra={`You can extend your bill by up to ${eligibility.maxExtensionBusinessDays} business days from its current due date.`}
                    rules={[
                        generateRequiredRule(null, 'Please select a date.'),
                        getRuleObject<Moment | null>(date => date && (!date.isValid() || isDisabledDate(date, calendar)) ? 'Invalid Date' : null)
                    ]}>
                    <DatePicker
                        id="billExtensionDueDate"
                        placeholder="Select date"
                        autoComplete="off"
                        mode="date"
                        style={{ width: '100%' }}
                        defaultPickerValue={moment(calendar.minDate, CalendarControlDateFormat)}
                        disabledDate={(date) => isDisabledDate(date, calendar)}
                        onChange={(date) => setNewDueDate(date)} />
                </Form.Item>
                {paymentAssistance
                    ? <Card bordered={false} bodyStyle={{ padding: 16 }} className={styles.assistance} role="region" aria-labelledby="paymentAssistanceTitle">
                        <p id="paymentAssistanceTitle"><b><InfoCircleOutlined className={styles.assistanceIcon} /> Payment assistance options</b></p>
                        {paymentAssistance.map(paragraph => <p key={paragraph}>{paragraph}</p>)}
                        <p>More information is available in GloBird's Hardship Policy at{' '}
                            <a href={links.corporateWebsite.legal} target="_blank" rel="noopener noreferrer">globirdenergy.com.au/legal</a>, or you can ask us for a free copy.</p>
                        <p>To access any of these options, speak with us.</p>
                    </Card>
                    : null}
                <Form.Item>
                    <Button
                        type="primary"
                        size="large"
                        htmlType="submit"
                        className="form-save-button"
                        disabled={!newDueDate?.isValid() || isDisabledDate(newDueDate, calendar) || submitting || submissionResult === 'success'}
                        loading={submitting}>
                        Extend Bill
                    </Button>
                </Form.Item>
            </Form>
        </>;
    };

    const renderSubmissionResult = () => {
        if (submissionResult === 'success') {
            return <Result
                status="success"
                title="Bill due date has been extended."
                subTitle={<>Please ensure full payment by the new due date. If you have any questions, please <Link to="/assistance">contact us</Link>.</>} />;
        }

        if (submissionResult === 'error') {
            return <Result status="error" title="Bill extension failed." subTitle={unavailableMessage} />;
        }

        return null;
    };

    return <div className="form-container">
        <div className="globird-menu-option">
            <p className="globird-menu-label">Show me: </p>
            <GlobirdSelector
                id="billMenuSelector"
                options={menuOptions}
                defaultOption={menuOptions[3]}
                onSelected={(targetMenu) => navigate(`/${targetMenu.value}`)} />
        </div>

        {renderContent()}

        <Modal
            className={styles.resultModal}
            visible={submissionResult !== undefined}
            closable={false}
            maskClosable={false}
            keyboard={false}
            centered
            footer={[
                <Button key="ok" className="globird-button-primary" style={{ minWidth: '150px', margin: '0 auto', display: 'block' }} onClick={() => {
                    if (submissionResult === 'success') {
                        navigate('/dashboard');
                    }
                    else {
                        setSubmissionResult(undefined);
                    }
                }}>OK</Button>
            ]}>
            {renderSubmissionResult()}
        </Modal>

    </div>;
};

export default BillExtension;
